import { useEffect, useId, useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { GPTModelItem } from "@/api/models"
import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"

import { ChevronDown, Clock, Sparkles } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { EffortSlider } from "./effort-slider"
import {
  groupLegacyModels,
  isCurrentModel,
  modelFamilyHint,
  sortCurrentModels,
} from "./reasoning-model-utils"
import {
  applyModelPick,
  modelShortcutHint,
  resolveActiveEffort,
} from "../utils/model-toggle-shortcuts"

type ReasoningModePillProps = {
  // Legacy reasoning-"mode" props. The abstract mode is no longer rendered — the
  // selector is model-first — but `mode` still gates model-pick effort resets
  // (keep slash-preset locks; clear only in auto). `onModeChange` is unused.
  mode?: ReasoningMode
  onModeChange?: (mode: ReasoningMode) => void
  // Model selection + reasoning effort — the real controls.
  availableModels?: GPTModelItem[]
  /** The model that will actually run (override, else the user's default). Drives
   *  the trigger label and the effort slider's stops. */
  selectedModel?: GPTModelItem
  modelOverride?: string | null
  onModelOverrideChange?: (modelId: string | null) => void
  /** Optional host-owned model selection handler. This bypasses the legacy
   *  split model/effort callbacks so persistent editors can update both fields
   *  atomically. Existing chat callers keep the default behavior. */
  onModelPick?: (modelId: string) => void
  effortOverride?: ReasoningEffortOverride | null
  onEffortOverrideChange?: (effort: ReasoningEffortOverride | null) => void
  disabled?: boolean
  /** Host-provided trigger text for invalid/stale persisted selections. */
  triggerLabelOverride?: string
  /** Marks the trigger as requiring attention while keeping the menu usable. */
  invalid?: boolean
  /** Id of host-rendered validation text announced for an invalid picker. */
  ariaDescribedBy?: string
  /** Extra classes for the dropdown panel (e.g. raise z-index above a dialog). */
  contentClassName?: string
  /** Set false to hide the reasoning-effort slider block and its "no reasoning
   *  effort" footer note entirely — for callers (e.g. admin config pickers)
   *  where effort selection doesn't apply. Defaults to true. */
  showEffortSection?: boolean
  /** Render the Alt+0–6 / Alt+± kbd hints. Only the chat composer passes true —
   *  it is the one surface that installs useModelToggleShortcuts; other pickers
   *  (admin dialogs, segment analysis) must not advertise dead shortcuts.
   *  Defaults to false. */
  showShortcutHints?: boolean
}

export function ReasoningModePill({
  mode,
  availableModels = [],
  selectedModel,
  modelOverride,
  onModelOverrideChange,
  onModelPick,
  effortOverride,
  onEffortOverrideChange,
  disabled = false,
  triggerLabelOverride,
  invalid = false,
  ariaDescribedBy,
  contentClassName,
  showEffortSection = true,
  showShortcutHints = false,
}: ReasoningModePillProps) {
  const { t } = useTranslation()
  const radioGroupName = useId()

  const currentModels = sortCurrentModels(availableModels.filter(isCurrentModel))
  const legacyModels = availableModels.filter((m) => !isCurrentModel(m))
  const legacyGroups = groupLegacyModels(legacyModels)

  // Memoized so the array reference is stable across renders (a fresh `[]` each
  // render would make the clamp effect below re-run every render).
  const supportedEfforts = useMemo(
    () => selectedModel?.supported_efforts ?? [],
    [selectedModel?.supported_efforts]
  )
  // In Auto the router picks both model and effort per question (see
  // `_normalize_reasoning_preset` in explorer_query_classifier.py: "Auto never
  // hard-locks effort"). A slider there would be a lie, so it only appears
  // once an explicit model is selected.
  const isAutoModel = selectedModel?.id === "auto"
  const hasEffortControl =
    showEffortSection &&
    !isAutoModel &&
    Boolean(onEffortOverrideChange) &&
    supportedEfforts.length > 0

  // Effort the slider currently reflects — shared with the Alt+± keyboard
  // shortcuts so the slider and the shortcut always step from the same value.
  const activeEffort: ReasoningEffortOverride | undefined = resolveActiveEffort(
    supportedEfforts,
    effortOverride,
    selectedModel?.default_effort
  )

  // When the model changes, an override the new model can't do would be silently
  // ignored by the backend — clear it so the slider snaps to the new default.
  useEffect(() => {
    if (effortOverride && !supportedEfforts.includes(effortOverride)) {
      onEffortOverrideChange?.(null)
    }
  }, [selectedModel?.id, effortOverride, supportedEfforts, onEffortOverrideChange])

  const triggerLabel =
    triggerLabelOverride ??
    selectedModel?.display_name ??
    t("reasoning_mode.model_auto", { defaultValue: "Auto" })
  // An effort override is inert in Auto (not shown, not sent), so it must not
  // light the pill up as customised.
  const hasCustom = modelOverride != null || (effortOverride != null && !isAutoModel)

  const handleModelPick = (modelId: string) => {
    if (onModelPick) {
      onModelPick(modelId)
      return
    }
    // Shared with the Alt+digit shortcut — see applyModelPick for the
    // effort-lock/reset semantics.
    applyModelPick(modelId, mode, {
      onModelOverrideChange: (id) => onModelOverrideChange?.(id),
      onEffortOverrideChange,
    })
  }

  const modelSubtitle = (modelId: string): string | null => {
    const family = modelFamilyHint(modelId)
    if (family === null) return null
    switch (family) {
      case "auto":
        return t("reasoning_mode.model_hint_auto", { defaultValue: "Picks for you" })
      case "luna":
        return t("reasoning_mode.model_hint_luna", { defaultValue: "Fastest" })
      case "terra":
        return t("reasoning_mode.model_hint_terra", { defaultValue: "Balanced" })
      case "sol":
        return t("reasoning_mode.model_hint_sol", { defaultValue: "Highest quality" })
      default: {
        const _exhaustive: never = family
        return _exhaustive
      }
    }
  }

  const renderModelRow = (model: GPTModelItem) => {
    const isSelected = (selectedModel?.id ?? modelOverride) === model.id
    const subtitle = modelSubtitle(model.id)
    // Only current-tier rows are shortcut targets (legacy ids could
    // coincidentally contain a family name — no hint there).
    const shortcutHint =
      showShortcutHints && isCurrentModel(model) ? modelShortcutHint(model.id, currentModels) : null
    return (
      <label
        key={model.id}
        // Light: accent + accent-foreground. Dark: muted surface (same pattern as
        // slash-command-menu) — accent is a pale blue that flashes too hard.
        className="hover:bg-accent hover:text-accent-foreground focus-within:ring-ring dark:hover:bg-muted dark:hover:text-foreground group flex w-full cursor-pointer items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm focus-within:ring-2 focus-within:outline-none"
      >
        <input
          aria-keyshortcuts={shortcutHint ?? undefined}
          checked={isSelected}
          className="sr-only"
          name={radioGroupName}
          type="radio"
          value={model.id}
          onChange={() => handleModelPick(model.id)}
        />
        <span
          aria-hidden="true"
          className={cn(
            "mt-0.5 size-3.5 shrink-0 rounded-full border",
            isSelected ? "border-primary bg-primary" : "border-muted-foreground/40"
          )}
        />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block truncate",
              isSelected
                ? "text-primary group-hover:text-accent-foreground dark:group-hover:text-foreground font-semibold"
                : undefined
            )}
          >
            {model.display_name}
          </span>
          {subtitle && (
            <span className="text-muted-foreground group-hover:text-accent-foreground/70 dark:group-hover:text-muted-foreground block truncate text-xs">
              {subtitle}
            </span>
          )}
        </span>
        {shortcutHint && (
          <kbd className="text-muted-foreground/70 border-border mt-0.5 shrink-0 rounded border px-1 font-sans text-[10px]">
            {shortcutHint}
          </kbd>
        )}
      </label>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-describedby={ariaDescribedBy}
          data-invalid={invalid || undefined}
          data-testid="model-selector-pill"
          disabled={disabled}
          type="button"
          className={cn(
            buttonVariants({ size: "md", variant: "ghost" }),
            "text-muted-foreground hover:text-accent-foreground h-7 gap-1.5 px-2",
            hasCustom && "text-primary hover:text-primary",
            invalid && "text-destructive hover:text-destructive"
          )}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Sparkles className="size-3.5" />
          <span className="max-w-[9rem] truncate">{triggerLabel}</span>
          <ChevronDown className="size-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("max-h-[70vh] w-[268px] overflow-y-auto p-1", contentClassName)}
        side="top"
        sideOffset={6}
        // The panel is portalled, but React still routes its events through
        // this component's place in the tree — straight into whatever the host
        // wraps the pill in. Chat composers wrap it in a click handler that
        // preventDefault()s (which would swallow the row radio's activation)
        // and refocuses the textarea (which closes this panel), so keep the
        // panel's own clicks and keys to itself.
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <fieldset>
          <legend className="text-muted-foreground px-2 py-1.5 text-xs font-normal">
            {t("reasoning_mode.model_label", { defaultValue: "Model" })}
          </legend>
          <div>{currentModels.map(renderModelRow)}</div>

          {legacyModels.length > 0 && (
            <details className="group/legacy">
              <summary className="focus:bg-accent focus:text-accent-foreground flex cursor-pointer list-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none">
                <Clock className="text-muted-foreground size-4" />
                <span>{t("reasoning_mode.legacy", { defaultValue: "Legacy" })}</span>
                <ChevronDown className="ml-auto size-3 transition-transform group-open/legacy:rotate-180" />
              </summary>
              <div className="border-border ml-2 border-l pl-1">
                {legacyGroups.map((group, groupIndex) => (
                  <div key={group.key}>
                    {groupIndex > 0 && <div className="bg-muted -mx-1 my-1 h-px" />}
                    <div className="text-muted-foreground px-2 py-1.5 text-xs font-normal">
                      {t(group.labelKey, { defaultValue: group.labelDefault })}
                    </div>
                    {group.models.map(renderModelRow)}
                  </div>
                ))}
              </div>
            </details>
          )}
        </fieldset>

        {hasEffortControl && (
          <>
            <div className="bg-muted -mx-1 my-1 h-px" />
            <div className="px-2.5 pt-2 pb-3">
              <EffortSlider
                disabled={disabled}
                efforts={supportedEfforts}
                shortcutHint={showShortcutHints ? "Alt +/−" : undefined}
                value={activeEffort ?? ""}
                onChange={(effort) => onEffortOverrideChange?.(effort as ReasoningEffortOverride)}
              />
            </div>
          </>
        )}

        {isAutoModel && showEffortSection && (
          <>
            <div className="bg-muted -mx-1 my-1 h-px" />
            <div
              className="text-muted-foreground px-2 py-1.5 text-xs"
              data-testid="effort-auto-hint"
            >
              {t("reasoning_mode.effort_auto_hint", {
                defaultValue: "Auto picks the model and reasoning effort for each question.",
              })}
            </div>
          </>
        )}

        {!hasEffortControl && !isAutoModel && showEffortSection && selectedModel && (
          <>
            <div className="bg-muted -mx-1 my-1 h-px" />
            <div className="text-muted-foreground px-2 py-1.5 text-xs">
              {t("reasoning_mode.no_effort", {
                defaultValue: "This model answers directly — no reasoning effort.",
              })}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
